import React, { useState, useCallback } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CFormLabel,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import Swal from 'sweetalert2'
import { API_URLS } from 'src/util/apiUrls'
import { encryptPassword } from 'src/util/encryption'
import { useNavigate } from 'react-router-dom'
import { validatePassword } from 'src/validation'
import { generateMathCaptcha, MathCaptcha } from 'src/util/generateCaptcha'

const ForgetPassword = () => {
  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [otpSent, setOtpSent] = useState(false)
  const [isOtpValid, setIsOtpValid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [captcha, setCaptcha] = useState(generateMathCaptcha())
  const [userCaptcha, setUserCaptcha] = useState('')
  const navigate = useNavigate()

  const handleFieldChange = useCallback((e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Real-time password validation
    if (name === 'newPassword') {
      const passwordError = validatePassword(value)
      setErrors((prev) => ({ ...prev, newPassword: passwordError }))
    }
  }, [])

  const validateCaptcha = useCallback(() => {
    return parseInt(userCaptcha) === captcha.answer
  }, [userCaptcha, captcha.answer])

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateMathCaptcha())
    setUserCaptcha('')
  }, [])

  const handleSendOtp = useCallback(async () => {
    if (!formData.email || !formData.mobile) {
      Swal.fire('Error', 'Both Email and Mobile are required!', 'error')
      return
    }

    if (!validateCaptcha()) {
      Swal.fire('Error', 'Incorrect CAPTCHA answer!', 'error')
      refreshCaptcha()
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch(API_URLS.FORGET_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Username: formData.email,
          Mobile: formData.mobile,
        }),
      })

      const result = await response.json()

      if (response.ok && result.IsSuccess) {
        Swal.fire('Success', 'OTP sent successfully!', 'success')
        setOtpSent(true)
      } else {
        Swal.fire('Error', result.Message || 'Failed to send OTP.', 'error')
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'An unexpected error occurred.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }, [formData.email, formData.mobile, validateCaptcha, refreshCaptcha])

  const handleValidateOtp = useCallback(async () => {
    if (!formData.otp) {
      Swal.fire('Error', 'OTP is required!', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch(API_URLS.VERIFY_OTP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Email: formData.email,
          MobileOTP: formData.otp,
          EmailOTP: formData.otp,
        }),
      })
      const result = await response.json()
      if (response.ok) {
        Swal.fire('Success', 'OTP verified successfully!', 'success')
        setIsOtpValid(true)
      } else {
        throw new Error(result?.message || 'Invalid OTP.')
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'An unexpected error occurred.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }, [formData.email, formData.otp])

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      if (formData.newPassword !== formData.confirmPassword) {
        Swal.fire('Error', 'Passwords do not match!', 'error')
        return
      }

      const passwordError = validatePassword(formData.newPassword)
      if (passwordError) {
        setErrors({ newPassword: passwordError })
        return
      }

      setIsSubmitting(true)
      try {
        const encryptedNewPassword = encryptPassword(formData.newPassword)
        const response = await fetch(API_URLS.UPDATE_PASSWORD, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            Email: formData.email,
            NewPassword: encryptedNewPassword,
          }),
        })
        const result = await response.json()
        if (response.ok) {
          Swal.fire('Success', 'Password updated successfully!', 'success')
          navigate('/')
          setOtpSent(false)
          setIsOtpValid(false)
        } else {
          throw new Error(result?.message || 'Failed to update password.')
        }
      } catch (error) {
        Swal.fire('Error', error.message || 'An unexpected error occurred.', 'error')
      } finally {
        setIsSubmitting(false)
      }
    },
    [formData.newPassword, formData.confirmPassword, formData.email, navigate],
  )

  const handleLogin = useCallback(() => {
    navigate('/')
  }, [navigate])

  return (
    <CContainer className="d-flex justify-content-center align-items-center min-vh-100">
      <CRow className="w-100">
        <CCol md={12} lg={10} xl={8} className="mx-auto">
          <CCard className="p-4 shadow-sm">
            <CCardHeader className="text-center fs-4 fw-bold">Forget Password</CCardHeader>
            <CCardBody>
              <CForm onSubmit={handleSubmit}>
                {!otpSent && !isOtpValid && (
                  <>
                    <div className="mb-4">
                      <CFormLabel>Email</CFormLabel>
                      <CFormInput
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleFieldChange}
                        required
                        size="lg"
                      />
                    </div>
                    <div className="mb-4">
                      <CFormLabel>Mobile</CFormLabel>
                      <CFormInput
                        type="text"
                        name="mobile"
                        placeholder="Enter your mobile number"
                        value={formData.mobile}
                        onChange={handleFieldChange}
                        required
                        size="lg"
                      />
                    </div>
                    <MathCaptcha
                      captcha={captcha}
                      userCaptcha={userCaptcha}
                      setUserCaptcha={setUserCaptcha}
                      refreshCaptcha={refreshCaptcha}
                    />
                  </>
                )}
                {otpSent && !isOtpValid && (
                  <div className="mb-4">
                    <CFormLabel>OTP</CFormLabel>
                    <CFormInput
                      type="text"
                      name="otp"
                      placeholder="Enter OTP"
                      value={formData.otp}
                      onChange={handleFieldChange}
                      required
                      size="lg"
                    />
                  </div>
                )}
                {isOtpValid && (
                  <>
                    <div className="mb-4">
                      <CFormLabel>New Password</CFormLabel>
                      <CFormInput
                        type="password"
                        name="newPassword"
                        placeholder="Enter new password"
                        value={formData.newPassword}
                        onChange={handleFieldChange}
                        required
                        size="lg"
                        className={errors.newPassword ? 'is-invalid' : ''}
                      />
                      {errors.newPassword && (
                        <div className="invalid-feedback d-block">{errors.newPassword}</div>
                      )}
                      {!errors.newPassword && formData.newPassword && (
                        <div className="valid-feedback d-block">Password meets requirements</div>
                      )}
                    </div>
                    <div className="mb-4">
                      <CFormLabel>Confirm Password</CFormLabel>
                      <CFormInput
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm new password"
                        value={formData.confirmPassword}
                        onChange={handleFieldChange}
                        required
                        size="lg"
                        className={
                          formData.confirmPassword &&
                          formData.newPassword !== formData.confirmPassword
                            ? 'is-invalid'
                            : formData.confirmPassword
                              ? 'is-valid'
                              : ''
                        }
                      />
                      {formData.confirmPassword &&
                        formData.newPassword !== formData.confirmPassword && (
                          <div className="invalid-feedback d-block">Passwords do not match</div>
                        )}
                    </div>
                  </>
                )}
                <div className="text-center d-flex justify-content-center gap-3">
                  {!otpSent && !isOtpValid ? (
                    <>
                      <CButton
                        type="button"
                        color="primary"
                        size="lg"
                        onClick={handleSendOtp}
                        disabled={
                          isSubmitting || !formData.email || !formData.mobile || !userCaptcha
                        }
                      >
                        {isSubmitting ? <CSpinner size="sm" className="me-2" /> : null}
                        Send OTP
                      </CButton>
                      <CButton type="button" color="primary" size="lg" onClick={handleLogin}>
                        Login
                      </CButton>
                    </>
                  ) : otpSent && !isOtpValid ? (
                    <CButton
                      type="button"
                      color="primary"
                      size="lg"
                      onClick={handleValidateOtp}
                      disabled={isSubmitting || !formData.otp}
                    >
                      {isSubmitting ? <CSpinner size="sm" className="me-2" /> : null}
                      Validate OTP
                    </CButton>
                  ) : (
                    <CButton
                      type="submit"
                      color="primary"
                      size="lg"
                      disabled={
                        isSubmitting ||
                        !formData.newPassword ||
                        !formData.confirmPassword ||
                        errors.newPassword ||
                        formData.newPassword !== formData.confirmPassword
                      }
                    >
                      {isSubmitting ? <CSpinner size="sm" className="me-2" /> : null}
                      Update Password
                    </CButton>
                  )}
                </div>
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  )
}

export default React.memo(ForgetPassword)
